import type { ChangeEvent } from "react";
import { format, isValid, parse } from "date-fns";
import { Calendar } from "primereact/calendar";
import type { FormEvent as PrimeFormEvent } from "primereact/ts-helpers";
import {
  financeDateRange,
  financeToday,
  type FinanceDatePreset,
  type FinanceDateRangeValue,
} from "../../finance/dates";

interface FinanceDateFilterProps {
  value: FinanceDateRangeValue;
  onChange: (value: FinanceDateRangeValue) => void;
}

const parseDate = (value: string): Date | null => {
  const date = parse(value, "yyyy-MM-dd", new Date());
  return isValid(date) ? date : null;
};

const formatDate = (value: Date): string => format(value, "yyyy-MM-dd");

export default function FinanceDateFilter({
  value,
  onChange,
}: FinanceDateFilterProps) {
  const setPreset = (event: ChangeEvent<HTMLSelectElement>) => {
    const preset = event.target.value as FinanceDatePreset;

    if (preset === "custom") {
      onChange({ ...value, preset });
      return;
    }

    onChange(financeDateRange(preset));
  };

  const setDate = (field: "from" | "to", event: PrimeFormEvent<Date>) => {
    if (!(event.value instanceof Date)) {
      return;
    }

    onChange({ ...value, [field]: formatDate(event.value), preset: "custom" });
  };

  const today = financeToday();
  const fromDate = parseDate(value.from);
  const toDate = parseDate(value.to);
  const fromMaxDate = toDate && toDate < today ? toDate : today;
  const calendarClassName = "mt-1 block w-full sm:w-40";
  const calendarInputClassName =
    "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-accent focus:ring-1 focus:ring-accent";

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:shrink-0 sm:flex-row sm:flex-nowrap sm:items-end">
      <label className="block text-sm font-medium text-slate-700 sm:shrink-0">
        Period
        <select
          className="mt-1 block h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm sm:w-36"
          value={value.preset}
          onChange={setPreset}
        >
          <option value="this_month">This month</option>
          <option value="last_month">Last month</option>
          <option value="this_quarter">This quarter</option>
          <option value="this_year">This year</option>
          <option value="custom">Custom range</option>
        </select>
      </label>

      <label className="block text-sm font-medium text-slate-700 sm:shrink-0">
        From
        <Calendar
          inputId="finance-date-from"
          className={calendarClassName}
          inputClassName={calendarInputClassName}
          inputStyle={{ width: "100%" }}
          value={fromDate}
          maxDate={fromMaxDate}
          dateFormat="yy-mm-dd"
          panelClassName="finance-date-picker-panel"
          onChange={(event) => setDate("from", event)}
        />
      </label>

      <label className="block text-sm font-medium text-slate-700 sm:shrink-0">
        To
        <Calendar
          inputId="finance-date-to"
          className={calendarClassName}
          inputClassName={calendarInputClassName}
          inputStyle={{ width: "100%" }}
          value={toDate}
          minDate={fromDate ?? undefined}
          maxDate={today}
          dateFormat="yy-mm-dd"
          panelClassName="finance-date-picker-panel"
          onChange={(event) => setDate("to", event)}
        />
      </label>
    </div>
  );
}
